import React, { useEffect, useState, useCallback } from 'react';
import { useAnalysisStatus } from '../hooks/useAnalysisStatus';
import API_URL from '../config';
import './NCProcessing.css';

/**
 * Componente para exibir NC em processamento (202 Async Response)
 *
 * Props:
 * - ncId: ID da NC sendo processada
 * - taskId: ID da task para polling
 * - numeroNC: Número da NC gerado
 * - onCompleted: callback quando análise termina
 * - onError: callback se der erro
 */
function NCProcessing({ ncId, taskId, numeroNC, onCompleted, onError }) {
  const [localError, setLocalError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [pollingEnabled, setPollingEnabled] = useState(true);

  // Usar hook de polling que já existe no projeto
  const { status, progress, error } = useAnalysisStatus(ncId, pollingEnabled, 2000);

  // FIX(F6): retry async — chama o novo endpoint /reanalisar-async, que
  // preserva todos os dados de entrada e só refaz a análise de IA.
  const handleRetry = async () => {
    setRetrying(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/nc/${ncId}/reanalisar-async`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      // Limpar erro e reativar polling para o novo task
      setLocalError(null);
      setPollingEnabled(true);
    } catch (err) {
      console.error('Erro ao reenviar para análise:', err);
      setLocalError(`Falha ao reenviar: ${err.message}`);
    } finally {
      setRetrying(false);
    }
  };

  // Função para carregar NC completa
  const loadNCAndComplete = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const res = await fetch(`${API_URL}/api/nc/${ncId}`, { headers });
      if (!res.ok) {
        throw new Error('Erro ao carregar NC completa');
      }

      const ncData = await res.json();
      if (onCompleted) {
        onCompleted(ncData);
      }
    } catch (err) {
      console.error('Erro ao carregar NC:', err);
      setLocalError('Erro ao carregar resultado. Recarregue a página.');
      if (onError) {
        onError(err.message);
      }
    }
  }, [ncId, onCompleted, onError]);

  // Quando completar, carregar NC e chamar onCompleted.
  // #216: na FALHA não chamamos mais onError — o App desmontava esta tela
  // (setView('list')) antes de o usuário ver o card de erro com "Tentar
  // Novamente". O card fica; onError segue reservado pra erro de CARREGAMENTO
  // do resultado (loadNCAndComplete).
  useEffect(() => {
    if (status === 'completed') {
      loadNCAndComplete();
    } else if (status === 'failed') {
      setLocalError(error || 'Análise falhou. Tente novamente.');
    }
  }, [status, error, loadNCAndComplete]);

  // Mostrar erro se houver
  if (localError) {
    return (
      <div className="nc-processing-error">
        <div className="processing-card error">
          <h2>
            <span className="material-symbols-outlined" style={{ fontSize: '22px', verticalAlign: 'text-bottom' }}>error</span> Erro na Análise
          </h2>
          <p className="error-message">{localError}</p>
          <p style={{ fontSize: '13px', color: '#64748b', marginTop: '-12px', marginBottom: '20px' }}>
            Os dados da NC <strong>{numeroNC || `#${ncId}`}</strong> foram preservados.
            Você pode tentar a análise novamente sem reenviar tudo.
          </p>
          <div className="error-actions">
            <button className="btn-primary" onClick={handleRetry} disabled={retrying}>
              {retrying ? (
                'Reenviando...'
              ) : (
                <>
                  <span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'text-bottom' }}>refresh</span> Tentar Novamente
                </>
              )}
            </button>
            <button className="btn-secondary" onClick={() => window.location.href = '/ncs'} disabled={retrying}>
              Voltar para NCs
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Sessão expirou durante a espera (poll recebeu 401/403). Antes o spinner girava
  // pra sempre — agora mostramos um card claro com ação de relogar. Os dados da NC
  // ficam preservados; a análise pode ter seguido no servidor.
  if (status === 'session_expired') {
    return (
      <div className="nc-processing-error">
        <div className="processing-card error">
          <h2>
            <span className="material-symbols-outlined" style={{ fontSize: '22px', verticalAlign: 'text-bottom' }}>lock</span> Sessão expirada
          </h2>
          <p className="error-message">{error || 'Sua sessão expirou. Faça login novamente para continuar.'}</p>
          <p style={{ fontSize: '13px', color: '#64748b', marginTop: '-12px', marginBottom: '20px' }}>
            Os dados da NC <strong>{numeroNC || `#${ncId}`}</strong> foram preservados.
          </p>
          <div className="error-actions">
            <button
              className="btn-primary"
              onClick={() => {
                try { localStorage.removeItem('token'); } catch (e) { /* noop */ }
                window.location.href = '/';
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'text-bottom' }}>login</span> Fazer login novamente
            </button>
            <button className="btn-secondary" onClick={() => window.location.href = '/ncs'}>
              Voltar para NCs
            </button>
          </div>
        </div>
      </div>
    );
  }

  // #216: timeout do poll ≠ falha — card paciente (não estilo erro): a análise
  // continua no servidor; o usuário pode conferir o resultado ou sair.
  if (status === 'timeout') {
    return (
      <div className="nc-processing-container">
        <div className="processing-card">
          <h2>
            <span className="material-symbols-outlined" style={{ fontSize: '22px', verticalAlign: 'text-bottom' }}>hourglass_top</span> Ainda processando…
          </h2>
          <p style={{ color: '#475569' }}>{error}</p>
          <div className="error-actions">
            <button className="btn-primary" onClick={() => loadNCAndComplete()}>
              <span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'text-bottom' }}>visibility</span> Verificar Resultado
            </button>
            <button className="btn-secondary" onClick={() => window.location.href = '/ncs'}>
              Voltar para NCs
            </button>
          </div>
        </div>
      </div>
    );
  }

  // #216: rótulo por ETAPA real (os valores de progress são marcos de fase,
  // não % de tempo — a chamada de IA vive entre 30 e 60 e é 90% da espera;
  // exibir "30% concluído" parado lia como travamento).
  const etapaLabel = () => {
    if (status === 'pending') return 'Na fila…';
    if (status === 'completed') return 'Concluído! Carregando…';
    if (status === 'cancelled') return 'Cancelado';
    const p = progress || 0;
    if (p >= 80) return 'Finalizando…';
    if (p >= 60) return 'Montando plano de ação e relatório…';
    if (p >= 30) return 'Analisando com IA — pode levar de 1 a 3 minutos…';
    return 'Preparando análise…';
  };

  return (
    <div className="nc-processing-container">
      <div className="processing-card">
        <div className="processing-header">
          <h1>
            <span className="material-symbols-outlined" style={{ fontSize: '26px', verticalAlign: 'text-bottom' }}>autorenew</span> Processando NC
          </h1>
          {numeroNC && <p className="nc-number">NC: {numeroNC}</p>}
        </div>

        <div className="processing-content">
          {/* Spinner animado */}
          <div className="spinner-container">
            <div className="spinner"></div>
          </div>

          {/* Etapa atual */}
          <div className="status-info">
            <p className="status-label">Status da Análise:</p>
            <p className="status-value">{etapaLabel()}</p>
          </div>

          {/* Mensagem informativa */}
          <div className="info-box">
            <p>
              Você pode sair desta página — a análise continua no servidor e a
              NC aparecerá na lista quando terminar.
            </p>
          </div>
        </div>
      </div>

      {/* Debug info (apenas em dev) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="debug-info">
          <details>
            <summary>Debug Info</summary>
            <pre>{JSON.stringify({ ncId, taskId, status, progress }, null, 2)}</pre>
          </details>
        </div>
      )}
    </div>
  );
}

export default NCProcessing;
